/**
 * SuperAdmin Audit Log Page
 * View all SuperAdmin actions
 */

import React, { useState, useEffect } from 'react';
import { logsApi } from '../utils/api';
import SuperadminLayout from '../components/SuperadminLayout';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  FunnelIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const actionColors = {
  login: 'bg-blue-50 text-blue-600',
  logout: 'bg-gray-50 text-gray-500',
  tenant_created: 'bg-green-50 text-green-600',
  tenant_updated: 'bg-yellow-50 text-yellow-600',
  tenant_suspended: 'bg-orange-50 text-orange-600',
  tenant_activated: 'bg-green-50 text-green-600',
  tenant_deleted: 'bg-red-50 text-red-600',
  tenant_impersonated: 'bg-purple-50 text-purple-600',
  admin_password_reset: 'bg-yellow-50 text-yellow-600',
  password_changed: 'bg-blue-50 text-blue-600'
};

const actionLabels = {
  login: 'Login',
  logout: 'Logout',
  tenant_created: 'Tenant Creado',
  tenant_updated: 'Tenant Actualizado',
  tenant_suspended: 'Tenant Suspendido',
  tenant_activated: 'Tenant Activado',
  tenant_deleted: 'Tenant Eliminado',
  tenant_impersonated: 'Impersonación',
  admin_password_reset: 'Reset Password',
  password_changed: 'Cambio Contraseña'
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [actions, setActions] = useState([]);
  const [targetType, setTargetType] = useState('');

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    try {
      const response = await logsApi.audit({
        page,
        limit: 50,
        action: actionFilter || undefined,
        target_type: targetType || undefined
      });
      setLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActions = async () => {
    try {
      const response = await logsApi.actions();
      setActions(response.data.actions);
    } catch (error) {
      console.error('Error fetching actions:', error);
    }
  };

  useEffect(() => {
    fetchActions();
  }, []);

  useEffect(() => {
    fetchLogs(1);
  }, [actionFilter, targetType]);

  const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleString('es-CR', { timeZone: 'America/Costa_Rica',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <SuperadminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Auditoría</h1>
            <p className="text-gray-500">Historial de acciones de SuperAdmin</p>
          </div>
          <button
            onClick={() => fetchLogs(pagination.page)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:bg-gray-100 rounded-lg text-gray-700 transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Actualizar
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex items-center gap-2 text-gray-500">
              <FunnelIcon className="w-5 h-5" />
              <span className="text-sm">Filtros:</span>
            </div>

            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas las acciones</option>
              {actions.map((action) => (
                <option key={action} value={action}>
                  {actionLabels[action] || action}
                </option>
              ))}
            </select>

            <select
              value={targetType}
              onChange={(e) => setTargetType(e.target.value)}
              className="bg-gray-100 border border-gray-300 rounded-lg px-4 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos los tipos</option>
              <option value="tenant">Tenant</option>
              <option value="user">Usuario</option>
              <option value="system">Sistema</option>
            </select>
          </div>
        </div>

        {/* Logs table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-gray-500 text-sm bg-gray-50">
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 font-medium">Usuario</th>
                  <th className="px-4 py-3 font-medium">Acción</th>
                  <th className="px-4 py-3 font-medium">Target</th>
                  <th className="px-4 py-3 font-medium">IP</th>
                  <th className="px-4 py-3 font-medium">Detalles</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      <ArrowPathIcon className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Cargando...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No hay logs disponibles
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="text-sm hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 text-gray-700">
                          <ClockIcon className="w-4 h-4 text-gray-500" />
                          {formatDate(log.created_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-gray-900">{log.superadmin_name}</p>
                          <p className="text-gray-500 text-xs">{log.superadmin_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionColors[log.action] || 'bg-gray-50 text-gray-500'}`}>
                          {actionLabels[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {log.target_name ? (
                          <div>
                            <p className="text-gray-900">{log.target_name}</p>
                            <p className="text-gray-500 text-xs capitalize">{log.target_type}</p>
                          </div>
                        ) : (
                          <span className="text-gray-500 capitalize">{log.target_type}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 font-mono text-xs">
                        {log.ip_address}
                      </td>
                      <td className="px-4 py-3">
                        {log.details && (
                          <button
                            onClick={() => {
                              console.log('Log details:', log.details);
                              alert(JSON.stringify(log.details, null, 2));
                            }}
                            className="text-gray-500 hover:text-gray-900 text-xs underline"
                          >
                            Ver detalles
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Página {pagination.page} de {pagination.pages} ({pagination.total} registros)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-white border border-gray-200 disabled:text-gray-600 text-gray-900 rounded text-sm transition-colors"
                >
                  Anterior
                </button>
                <button
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 disabled:bg-white border border-gray-200 disabled:text-gray-600 text-gray-900 rounded text-sm transition-colors"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </SuperadminLayout>
  );
}
